/**
 * useSystemPostTemplates — real-time subscription to the system platform-level
 *   system_platforms/{platformId}/post_templates/*
 * post-template catalogue. Used by the Templates browse tab in
 * ContentPlanPage. Read-only this iteration; clone-to-project + edit
 * land in a future iteration.
 */

import { useEffect, useState } from 'react';
import {
  getSystemPostTemplateService,
  type ListFilter,
} from '../services/SystemPostTemplateService';
import type { PostTemplate } from '@segmently/schema-models/content-plan';

export interface UseSystemPostTemplatesReturn {
  items: PostTemplate[];
  loading: boolean;
  error: Error | null;
}

export function useSystemPostTemplates(filter: ListFilter = {}): UseSystemPostTemplatesReturn {
  const [items, setItems] = useState<PostTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Stable string key for shallow-comparable effect deps
  const filterKey = JSON.stringify({
    g: filter.goal ?? null,
    p: filter.platform ?? null,
    t: filter.tone ?? null,
    l: filter.lengthBucket ?? null,
  });

  useEffect(() => {
    setLoading(true);
    setError(null);
    const svc = getSystemPostTemplateService();
    const unsub = svc.subscribe(
      filter,
      (next) => {
        setItems(next);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setItems([]);
        setLoading(false);
      },
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  return { items, loading, error };
}
